import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LayoutGrid, List, Plus, Target, Zap } from 'lucide-react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../authentication/AuthContext';
import AddTrackerModal from './components/AddTrackerModal';
import { useCreateTracker, useCreateTrackerEntry, useTrackerEntries, useTrackers, useTrackerScore } from './hooks/useTrackers';

function formatTarget(tracker) {
    if (tracker.type === 'BOOLEAN') return 'Boolean tracker';
    const targetValue = tracker.targetValue ?? tracker.numericTarget;
    if (targetValue == null || targetValue === '') return 'No target';
    const unit = tracker.targetUnit || tracker.unit ? ` ${tracker.targetUnit || tracker.unit}` : '';
    return `≥ ${targetValue}${unit}`;
}

function dateInputFromTimestamp(timestamp) {
    const date = timestamp ? new Date(timestamp) : new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function startOfDayTimestamp(dateInputValue) {
    const date = new Date(`${dateInputValue}T00:00:00`);
    return date.getTime();
}

function shiftDateInput(dateInputValue, amount) {
    const date = new Date(`${dateInputValue}T00:00:00`);
    date.setDate(date.getDate() + amount);
    return dateInputFromTimestamp(date.getTime());
}

function getScoreColor(percentage) {
    if (percentage >= 80) return { bar: 'bg-emerald-500', text: 'text-emerald-400', glow: 'shadow-emerald-500/20' };
    if (percentage >= 50) return { bar: 'bg-amber-500', text: 'text-amber-400', glow: 'shadow-amber-500/20' };
    return { bar: 'bg-red-500', text: 'text-red-400', glow: 'shadow-red-500/20' };
}

function ScoreWidget({ scoreData, isLoading, trackers }) {
    if (isLoading) {
        return (
            <div className="mb-5 animate-pulse rounded-xl border border-[var(--border-color)] bg-[var(--surface)] p-4 h-24" />
        );
    }

    if (!scoreData) return null;

    const totalScore = scoreData.total_score ?? scoreData.totalScore ?? 0;
    const maxScore = scoreData.max_score ?? scoreData.maxScore ?? 0;
    const percentage = scoreData.percentage ?? 0;
    const trackerScores = scoreData.tracker_scores ?? scoreData.trackerScores ?? [];
    const colors = getScoreColor(percentage);

    return (
        <div className={`mb-5 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] p-4 shadow-lg ${colors.glow}`}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--brand-coral)]/10 text-[var(--brand-coral)]">
                        <Zap size={15} />
                    </div>
                    <div>
                        <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--text-muted)]">Success Rate</p>
                        <div className="flex items-baseline gap-2">
                            <span className={`text-2xl font-bold ${colors.text}`}>{percentage}%</span>
                            <span className="text-xs text-[var(--text-muted)]">{totalScore} / {maxScore} pts</span>
                        </div>
                    </div>
                </div>

                {trackerScores.length > 0 && (
                    <div className="hidden sm:flex flex-wrap justify-end gap-1.5 max-w-sm">
                        {trackerScores.map((item) => {
                            const itemTrackerId = item.trackerId ?? item.tracker_id;
                            const itemScore = item.score ?? 0;
                            const itemMax = item.max_score ?? item.maxScore ?? 0;
                            const tracker = trackers.find((t) => t.id === itemTrackerId);
                            const itemPct = itemMax > 0 ? Math.round((itemScore / itemMax) * 100) : 0;
                            const dot = itemPct >= 100 ? 'bg-emerald-500' : itemPct > 0 ? 'bg-amber-500' : 'bg-[var(--border-strong)]';
                            return (
                                <div
                                    key={itemTrackerId}
                                    className="flex items-center gap-1 rounded-full border border-[var(--border-color)] bg-[var(--surface-deep)] px-2 py-0.5"
                                    title={`${tracker?.title ?? 'Tracker'}: ${itemScore}/${itemMax}`}
                                >
                                    <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                                    <span className="max-w-[80px] truncate text-[11px] text-[var(--text-muted)]">{tracker?.title ?? '—'}</span>
                                    <span className="text-[11px] text-[var(--text-muted)]">{itemScore}/{itemMax}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="h-1.5 rounded-full bg-[var(--surface-hover)] overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-700 ${colors.bar}`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                />
            </div>
        </div>
    );
}

function TrackerCard({ tracker, authToken, selectedDate }) {
    const navigate = useNavigate();
    const [numericInput, setNumericInput] = useState('');
    const [localEntry, setLocalEntry] = useState(null);
    const { data: entries = [] } = useTrackerEntries(authToken, tracker.id);
    const { mutate: saveEntry, isLoading: isSavingEntry } = useCreateTrackerEntry(authToken, tracker.id);

    const selectedTimestamp = useMemo(() => startOfDayTimestamp(selectedDate), [selectedDate]);
    const selectedEntry = useMemo(
        () => entries.find((entry) => dateInputFromTimestamp(entry.loggedDate) === selectedDate) ?? null,
        [entries, selectedDate]
    );
    const activeEntry = localEntry ?? selectedEntry;
    const isBooleanTracker = tracker.type === 'BOOLEAN';
    const numericTarget = Number(tracker.targetValue ?? tracker.numericTarget ?? 0);
    const numericValue = Number(activeEntry?.numericValue ?? 0);
    const isNumericSuccess = numericValue >= numericTarget;

    useEffect(() => {
        if (isBooleanTracker) return;
        setNumericInput(activeEntry?.numericValue ?? '');
    }, [activeEntry, isBooleanTracker]);

    useEffect(() => {
        setLocalEntry(selectedEntry);
    }, [selectedEntry, selectedDate]);

    const handleSaveNumeric = () => {
        saveEntry({
            id: activeEntry?.id,
            logged_date: selectedTimestamp,
            numeric_value: numericInput === '' ? null : Number(numericInput),
        }, {
            onSuccess: (savedEntry) => {
                setLocalEntry(savedEntry);
                setNumericInput(savedEntry?.numericValue ?? '');
            },
        });
    };

    const handleToggleBoolean = () => {
        const nextCompleted = !Boolean(activeEntry?.completed);
        saveEntry({
            id: activeEntry?.id,
            logged_date: selectedTimestamp,
            completed: nextCompleted,
        }, {
            onSuccess: (savedEntry) => {
                setLocalEntry(savedEntry);
            },
        });
    };

    return (
        <div className="group flex w-full flex-col gap-3 rounded-2xl border border-[var(--border-color)] bg-[var(--surface)] p-5 text-left transition-all duration-200 hover:border-[var(--brand-coral)]/50 hover:shadow-lg hover:shadow-[var(--brand-coral)]/5">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--brand-coral)]/10 text-[var(--brand-coral)]">
                        <Target size={18} />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-base font-semibold text-[var(--text-primary)] transition-colors group-hover:text-[var(--brand-coral)]">
                            {tracker.title || 'Untitled Tracker'}
                        </h3>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => navigate(`/tracker/${tracker.id}`)}
                    className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-color)] px-2 py-1 text-xs text-[var(--text-secondary)] transition hover:border-[var(--brand-coral)]/40 hover:text-[var(--brand-coral)]"
                >
                    Open
                    <ChevronRight size={14} className="shrink-0 text-[var(--text-muted)] transition-colors group-hover:text-[var(--brand-coral)]" />
                </button>
            </div>

            {tracker.description && (
                <p className="text-sm text-[var(--text-muted)] line-clamp-2">{tracker.description}</p>
            )}

            <div className="rounded-lg border border-[var(--border-color)] bg-[var(--surface-deep)] px-3 py-2">
                <p className="text-xs text-[var(--text-muted)]">
                    <span className="font-medium text-[var(--text-secondary)]">Selected day:</span>{' '}
                    {isBooleanTracker
                        ? (activeEntry ? (activeEntry.completed ? 'Completed' : 'Not completed') : 'No entry yet')
                        : (activeEntry
                            ? `${activeEntry.numericValue ?? 0}${tracker.targetUnit || tracker.unit ? ` ${tracker.targetUnit || tracker.unit}` : ''} (${isNumericSuccess ? 'Success' : 'Below target'})`
                            : 'No entry yet')}
                </p>
            </div>

            <div className="mt-1 rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] p-3">
                {isBooleanTracker ? (
                    <button
                        type="button"
                        onClick={handleToggleBoolean}
                        disabled={isSavingEntry}
                        className={`w-full rounded-lg px-3 py-2 text-sm font-medium transition ${activeEntry?.completed ? 'bg-green-600/20 text-green-300 hover:bg-green-600/30' : 'bg-[var(--brand-coral)]/20 text-[var(--brand-coral)] hover:bg-[var(--brand-coral)]/30'} disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                        {activeEntry?.completed ? 'Mark as not completed' : 'Mark as completed'}
                    </button>
                ) : (
                    <div className="flex items-end gap-2">
                        <button
                            type="button"
                            onClick={() => setNumericInput((prev) => String(Math.max(0, Number(prev || 0) - 1)))}
                            disabled={isSavingEntry}
                            className="rounded-lg border border-[var(--border-strong)] px-3 py-2 text-sm font-semibold text-[var(--text-secondary)] transition hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-60"
                            aria-label="Decrease value"
                        >
                            −
                        </button>
                        <div className="flex-1">
                            <label htmlFor={`tracker-value-${tracker.id}`} className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[var(--text-muted)]">
                                Value
                            </label>
                            <input
                                id={`tracker-value-${tracker.id}`}
                                type="number"
                                min="0"
                                value={numericInput}
                                onChange={(event) => setNumericInput(event.target.value)}
                                className="w-full rounded-lg bg-[var(--surface-hover)] px-2.5 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]"
                                placeholder="0"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => setNumericInput((prev) => String(Number(prev || 0) + 1))}
                            disabled={isSavingEntry}
                            className="rounded-lg border border-[var(--border-strong)] px-3 py-2 text-sm font-semibold text-[var(--text-secondary)] transition hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-60"
                            aria-label="Increase value"
                        >
                            +
                        </button>
                        <button
                            type="button"
                            onClick={handleSaveNumeric}
                            disabled={isSavingEntry}
                            className="rounded-lg bg-[var(--button-primary-bg)] px-3 py-2 text-sm font-semibold text-[var(--button-primary-text)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Save
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function TrackerListRow({ tracker, authToken, selectedDate }) {
    const navigate = useNavigate();
    const [numericInput, setNumericInput] = useState('');
    const [localEntry, setLocalEntry] = useState(null);
    const { data: entries = [] } = useTrackerEntries(authToken, tracker.id);
    const { mutate: saveEntry, isLoading: isSavingEntry } = useCreateTrackerEntry(authToken, tracker.id);

    const selectedTimestamp = useMemo(() => startOfDayTimestamp(selectedDate), [selectedDate]);
    const selectedEntry = useMemo(
        () => entries.find((entry) => dateInputFromTimestamp(entry.loggedDate) === selectedDate) ?? null,
        [entries, selectedDate]
    );
    const activeEntry = localEntry ?? selectedEntry;
    const isBooleanTracker = tracker.type === 'BOOLEAN';
    const numericTarget = Number(tracker.targetValue ?? tracker.numericTarget ?? 0);
    const numericValue = Number(activeEntry?.numericValue ?? 0);
    const isNumericSuccess = numericValue >= numericTarget;

    useEffect(() => {
        if (isBooleanTracker) return;
        setNumericInput(activeEntry?.numericValue ?? '');
    }, [activeEntry, isBooleanTracker]);

    useEffect(() => {
        setLocalEntry(selectedEntry);
    }, [selectedEntry, selectedDate]);

    const handleSaveNumeric = () => {
        saveEntry({
            id: activeEntry?.id,
            logged_date: selectedTimestamp,
            numeric_value: numericInput === '' ? null : Number(numericInput),
        }, {
            onSuccess: (savedEntry) => {
                setLocalEntry(savedEntry);
                setNumericInput(savedEntry?.numericValue ?? '');
            },
        });
    };

    const handleToggleBoolean = () => {
        const nextCompleted = !Boolean(activeEntry?.completed);
        saveEntry({
            id: activeEntry?.id,
            logged_date: selectedTimestamp,
            completed: nextCompleted,
        }, {
            onSuccess: (savedEntry) => setLocalEntry(savedEntry),
        });
    };

    return (
        <div className="group flex w-full items-center gap-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] px-4 py-3 transition-all duration-200 hover:border-[var(--brand-coral)]/50 hover:shadow-lg hover:shadow-[var(--brand-coral)]/5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--brand-coral)]/10 text-[var(--brand-coral)]">
                <Target size={15} />
            </div>

            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[var(--text-primary)] group-hover:text-[var(--brand-coral)] transition-colors truncate">
                        {tracker.title || 'Untitled Tracker'}
                    </span>
                    <span className="shrink-0 text-xs text-[var(--text-muted)]">
                        {isBooleanTracker ? 'Boolean' : formatTarget(tracker)}
                    </span>
                </div>
                {tracker.description && (
                    <p className="mt-0.5 text-xs text-[var(--text-muted)] truncate">{tracker.description}</p>
                )}
            </div>

            <div className="shrink-0">
                {isBooleanTracker ? (
                    <button
                        type="button"
                        onClick={handleToggleBoolean}
                        disabled={isSavingEntry}
                        className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${activeEntry?.completed ? 'bg-green-600/20 text-green-300 hover:bg-green-600/30' : 'bg-[var(--brand-coral)]/20 text-[var(--brand-coral)] hover:bg-[var(--brand-coral)]/30'} disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                        {activeEntry?.completed ? '✓ Done' : 'Mark done'}
                    </button>
                ) : (
                    <div className="flex items-center gap-1.5">
                        <span className={`text-xs ${isNumericSuccess ? 'text-green-400' : 'text-[var(--text-muted)]'}`}>
                            {activeEntry ? `${activeEntry.numericValue ?? 0}${tracker.targetUnit || tracker.unit ? ` ${tracker.targetUnit || tracker.unit}` : ''}` : '—'}
                        </span>
                        <button
                            type="button"
                            onClick={() => setNumericInput((prev) => String(Math.max(0, Number(prev || 0) - 1)))}
                            disabled={isSavingEntry}
                            className="rounded border border-[var(--border-strong)] px-2 py-1 text-xs text-[var(--text-secondary)] transition hover:bg-[var(--surface-hover)] disabled:opacity-60"
                        >−</button>
                        <input
                            type="number"
                            min="0"
                            value={numericInput}
                            onChange={(e) => setNumericInput(e.target.value)}
                            className="w-16 rounded bg-[var(--surface-hover)] px-2 py-1 text-center text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-coral)]"
                            placeholder="0"
                        />
                        <button
                            type="button"
                            onClick={() => setNumericInput((prev) => String(Number(prev || 0) + 1))}
                            disabled={isSavingEntry}
                            className="rounded border border-[var(--border-strong)] px-2 py-1 text-xs text-[var(--text-secondary)] transition hover:bg-[var(--surface-hover)] disabled:opacity-60"
                        >+</button>
                        <button
                            type="button"
                            onClick={handleSaveNumeric}
                            disabled={isSavingEntry}
                            className="rounded bg-[var(--button-primary-bg)] px-2.5 py-1 text-xs font-semibold text-[var(--button-primary-text)] transition hover:brightness-105 disabled:opacity-60"
                        >Save</button>
                    </div>
                )}
            </div>

            <button
                type="button"
                onClick={() => navigate(`/tracker/${tracker.id}`)}
                className="shrink-0 rounded-lg border border-[var(--border-color)] p-1.5 text-[var(--text-muted)] transition hover:border-[var(--brand-coral)]/40 hover:text-[var(--brand-coral)]"
            >
                <ChevronRight size={14} />
            </button>
        </div>
    );
}

export default function Trackers() {
    const { authToken } = useAuth();
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [viewMode, setViewMode] = useState('grid');
    const [selectedDate, setSelectedDate] = useState(() => dateInputFromTimestamp(Date.now()));
    const [pageSize, setPageSize] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    const { data: trackers = [], isLoading } = useTrackers(authToken);
    const { mutate: createTracker } = useCreateTracker(authToken);
    const totalPages = Math.max(1, Math.ceil(trackers.length / pageSize));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const pageStart = (safeCurrentPage - 1) * pageSize;
    const paginatedTrackers = trackers.slice(pageStart, pageStart + pageSize);

    const selectedTimestamp = useMemo(() => startOfDayTimestamp(selectedDate), [selectedDate]);
    const userTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
    const { data: scoreData, isLoading: isScoreLoading } = useTrackerScore(authToken, selectedTimestamp, userTimezone);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const handleSubmit = (trackerData) => {
        createTracker(trackerData, {
            onSuccess: (createdTracker) => {
                setShowModal(false);
                navigate(`/tracker/${createdTracker.id}`);
            },
        });
    };

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg)]">
            <Header />
            <div className="flex flex-1 gap-3 bg-[var(--workspace-bg)] px-3 pb-4 pt-[76px] md:gap-4 md:px-4">
                <Sidebar />
                <div className="min-w-0 flex-1 rounded-[10px] bg-[var(--surface)]">
                    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
                        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--text-primary)]">
                                    <Target size={24} className="text-[var(--brand-coral)]" />
                                    Trackers
                                </h1>
                                <p className="mt-1 text-sm text-[var(--text-muted)]">
                                    {trackers.length ? `${trackers.length} tracker(s)` : 'No trackers yet'}
                                </p>
                            </div>

                            <div className="flex items-center gap-2">
                                <div className="flex items-center rounded-lg border border-[var(--border-color)] bg-[var(--surface)] p-1">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('grid')}
                                        className={`rounded-md p-1.5 transition-colors ${viewMode === 'grid' ? 'bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                                        title="Grid view"
                                    >
                                        <LayoutGrid size={15} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('list')}
                                        className={`rounded-md p-1.5 transition-colors ${viewMode === 'list' ? 'bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                                        title="List view"
                                    >
                                        <List size={15} />
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(true)}
                                    className="inline-flex items-center gap-2 rounded-xl bg-[var(--button-primary-bg)] px-4 py-2.5 text-sm font-semibold text-[var(--button-primary-text)] shadow-lg  transition hover:brightness-105 active:brightness-95"
                                >
                                    <Plus size={16} />
                                    New Tracker
                                </button>
                            </div>
                        </div>

                        <div className="mb-5 flex flex-wrap items-end justify-between gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] px-4 py-3">
                            <div>
                                <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--text-muted)]">Entry date</p>
                                <p className="text-sm text-[var(--text-secondary)]">All quick updates will save to this date.</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setSelectedDate((prev) => shiftDateInput(prev, -1))}
                                    className="rounded-lg border border-[var(--border-color)] p-2 text-[var(--text-secondary)] transition hover:border-[var(--brand-coral)]/40 hover:text-[var(--brand-coral)]"
                                    aria-label="Previous day"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(event) => setSelectedDate(event.target.value)}
                                    className="rounded-lg bg-[var(--surface-hover)] px-2.5 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]"
                                />
                                <button
                                    type="button"
                                    onClick={() => setSelectedDate((prev) => shiftDateInput(prev, 1))}
                                    className="rounded-lg border border-[var(--border-color)] p-2 text-[var(--text-secondary)] transition hover:border-[var(--brand-coral)]/40 hover:text-[var(--brand-coral)]"
                                    aria-label="Next day"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>

                        {trackers.length > 0 && (
                            <ScoreWidget
                                scoreData={scoreData}
                                isLoading={isScoreLoading}
                                trackers={trackers}
                            />
                        )}

                        {isLoading && (
                            <div className={viewMode === 'grid' ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3' : 'flex flex-col gap-2'}>
                                {new Array(6).fill(null).map((_, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <div key={`tracker-skeleton-${index}`} className={`animate-pulse rounded-2xl border border-[var(--border-color)] bg-[var(--surface)] ${viewMode === 'grid' ? 'h-40 p-5' : 'h-14 px-4'}`} />
                                ))}
                            </div>
                        )}

                        {!isLoading && trackers.length > 0 && viewMode === 'grid' && (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {paginatedTrackers.map((tracker) => (
                                    <TrackerCard key={tracker.id} tracker={tracker} authToken={authToken} selectedDate={selectedDate} />
                                ))}
                            </div>
                        )}

                        {!isLoading && trackers.length > 0 && viewMode === 'list' && (
                            <div className="flex flex-col gap-2">
                                {paginatedTrackers.map((tracker) => (
                                    <TrackerListRow key={tracker.id} tracker={tracker} authToken={authToken} selectedDate={selectedDate} />
                                ))}
                            </div>
                        )}

                        {!isLoading && trackers.length > 0 && (
                            <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                                    <span>Rows per page</span>
                                    <select
                                        value={pageSize}
                                        onChange={(event) => {
                                            setPageSize(Number(event.target.value));
                                            setCurrentPage(1);
                                        }}
                                        className="rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] px-2 py-1 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--brand-coral)]"
                                        aria-label="Trackers per page"
                                    >
                                        {[25, 50, 100].map(size => (
                                            <option key={size} value={size}>{size}</option>
                                        ))}
                                    </select>
                                    <span>
                                        {pageStart + 1}–{Math.min(pageStart + pageSize, trackers.length)} of {trackers.length}
                                    </span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                                        disabled={safeCurrentPage === 1}
                                        className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-deep)] disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Previous
                                    </button>
                                    <span className="min-w-20 text-center text-xs text-[var(--text-muted)]">
                                        Page {safeCurrentPage} of {totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                                        disabled={safeCurrentPage === totalPages}
                                        className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-deep)] disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        {!isLoading && trackers.length === 0 && (
                            <div className="flex flex-col items-center justify-center gap-4 py-28">
                                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-[var(--border-color)] bg-[var(--surface)]">
                                    <Target size={28} className="text-[var(--text-muted)]" />
                                </div>
                                <div className="text-center">
                                    <h2 className="text-lg font-semibold text-[var(--text-secondary)]">No trackers yet</h2>
                                    <p className="mt-1 text-sm text-[var(--text-muted)]">Create your first tracker to start logging progress.</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(true)}
                                    className="mt-2 inline-flex items-center gap-2 rounded-xl bg-[var(--button-primary-bg)] px-4 py-2.5 text-sm font-semibold text-[var(--button-primary-text)] transition hover:brightness-105"
                                >
                                    <Plus size={16} />
                                    New Tracker
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showModal && (
                <AddTrackerModal
                    onClose={() => setShowModal(false)}
                    onSubmit={handleSubmit}
                />
            )}
        </div>
    );
}
